import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { patientAPI, labTestAPI, addressAPI, labTestOrderAPI, walletAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ageFromDob,
  defaultCatalogGenderFromPatient,
  toThyrocareContactNumber,
  formatLabTestApiErrorBody,
  formatSlotsSearchError,
  EMPTY_NEW_ADDR,
  mapPatientRecordToLabAddress,
  newAddrFormToLabAddress,
  extractSlots,
  slotStart,
  tomorrowISO,
  formatDisplayDate,
  generateTestCode,
  buildLabTestStorageRecord,
  buildLabTestOrderApiPayload,
} from '../utils/labTestUtils';

export function useLabTestBooking() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const patientIdFromUrl = searchParams.get('patientId');

  // ── Patient search ─────────────────────────────────────────────────────────
  const [patientQuery,    setPatientQuery]    = useState('');
  const [patientResults,  setPatientResults]  = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchLoading,   setSearchLoading]   = useState(false);
  const [showDropdown,    setShowDropdown]    = useState(false);
  const searchRef = useRef(null);

  // Pre-select the patient when arriving from their own detail page (e.g.
  // "+ Book Lab Test" on PatientDetailPage.js) — same pattern as
  // BookAppointmentPage.js's patientId handling, so a navigator who came
  // from a specific patient doesn't have to search for them again.
  useEffect(() => {
    if (patientIdFromUrl) {
      patientAPI.get(patientIdFromUrl)
        .then(r => setSelectedPatient(r.data))
        .catch(() => {});
    }
  }, [patientIdFromUrl]);

  // 27 Jul 2026: new — centralized here (rather than fetched separately by
  // OrderSummary/ReviewPayStep) so there's one source of truth shared by the
  // always-visible sticky sidebar, the review step, AND the submit button's
  // disabled state below. Deepu's UX feedback, 27 Jul 2026: the insufficient-
  // balance warning was easy to miss because it only appeared in inline
  // content that scrolls away, while the "Charge" button sits in a sticky
  // footer that's always visible — a navigator could click it without ever
  // scrolling to see why it would fail. Fix: surface the balance in the
  // ALSO-sticky Order Summary sidebar, and disable the button itself.
  const [walletBalanceMinorUnits, setWalletBalanceMinorUnits] = useState(null);
  useEffect(() => {
    if (!selectedPatient?.id) { setWalletBalanceMinorUnits(null); return; }
    walletAPI.getBalance(selectedPatient.id)
      .then(res => setWalletBalanceMinorUnits(res.data.balanceMinorUnits ?? 0))
      .catch(() => setWalletBalanceMinorUnits(null));
  }, [selectedPatient?.id]);

  // ── Catalog / Plans ────────────────────────────────────────────────────────
  const [skuList,        setSkuList]        = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError,   setCatalogError]   = useState('');
  const [page,           setPage]           = useState(1);
  const [isLastPage,     setIsLastPage]     = useState(true);
  const [priceMin,       setPriceMin]       = useState(0);
  const [priceMax,       setPriceMax]       = useState(10000);
  const [expandedSku,    setExpandedSku]    = useState(null);
  const [selectedSku,    setSelectedSku]    = useState(null);

  // ── Address & Slot ─────────────────────────────────────────────────────────
  const [address,                 setAddress]                = useState(() => mapPatientRecordToLabAddress(null));
  const [patientAddresses,        setPatientAddresses]       = useState([]);
  const [addressesLoading,        setAddressesLoading]       = useState(false);
  const [addressMode,             setAddressMode]            = useState('saved');
  const [selectedSavedAddressId,  setSelectedSavedAddressId] = useState(null);
  const [newAddrForm,             setNewAddrForm]            = useState(() => ({ ...EMPTY_NEW_ADDR }));
  const [savingPatientAddress,    setSavingPatientAddress]   = useState(false);
  const [addressFormMessage,      setAddressFormMessage]     = useState('');
  const [appointmentDate,         setAppointmentDate]        = useState(tomorrowISO());
  const [slots,                   setSlots]                  = useState([]);
  const [slotsLoading,            setSlotsLoading]           = useState(false);
  const [slotsError,              setSlotsError]             = useState('');
  const [selectedSlot,            setSelectedSlot]           = useState(null);

  // Prevents duplicate auto slot-searches for the same (plan + gender)
  const lastAutoSlotKeyRef = useRef('');

  // ── Post-payment phase ─────────────────────────────────────────────────────
  const [error,            setError]            = useState('');
  const [orderPhase,       setOrderPhase]       = useState('idle');
  const [orderAttempt,     setOrderAttempt]     = useState(0);
  const [savedRzp,         setSavedRzp]         = useState(null);
  const savedApiBodyRef    = useRef(null);
  const savedPartnerIdRef  = useRef(null);
  // 27 Jul 2026: new — navigator bookings are wallet-only now (Deepu, 27 Jul
  // 2026), so the refund handler needs to know whether to request a wallet
  // credit-back or a Razorpay refund. savedRzp being null used to be
  // ambiguous (could mean "not paid yet" OR "paid via wallet"); this ref
  // disambiguates explicitly.
  const paidViaWalletRef   = useRef(false);
  const [refundRequesting, setRefundRequesting] = useState(false);
  const [refundResult,     setRefundResult]     = useState(null);

  // 27 Jul 2026: new — same reasoning as BookAppointmentPage.js's identical
  // check. Checked once on mount so LabTestFormPage.js can show a clear
  // message up front if this is ever flipped without the Razorpay-enabled
  // path actually being built.
  const [razorpayEnabledForNavigator, setRazorpayEnabledForNavigator] = useState(false);
  useEffect(() => {
    walletAPI.getNavigatorConfig()
      .then(res => setRazorpayEnabledForNavigator(!!res.data.razorpayEnabled))
      .catch(() => setRazorpayEnabledForNavigator(false));
  }, []);

  // ── Patient search logic ───────────────────────────────────────────────────
  const searchPatients = useCallback(async (q) => {
    if (q.length < 2) { setPatientResults([]); setShowDropdown(false); return; }
    setSearchLoading(true);
    try {
      const res = await patientAPI.list({ search: q, size: 8 });
      const list = Array.isArray(res.data)
        ? res.data
        : (res.data?.patients || res.data?.content || []);
      setPatientResults(list);
      setShowDropdown(true);
    } catch {
      setPatientResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchPatients(patientQuery), 300);
    return () => clearTimeout(t);
  }, [patientQuery, searchPatients]);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Patient change → reset downstream + load addresses ────────────────────
  useEffect(() => {
    if (!selectedPatient) {
      setPatientAddresses([]);
      setSelectedSavedAddressId(null);
      setAddressMode('saved');
      setNewAddrForm({ ...EMPTY_NEW_ADDR });
      setAddress(mapPatientRecordToLabAddress(null));
      setAddressFormMessage('');
      setSkuList([]); setSelectedSku(null); setExpandedSku(null);
      setSlots([]); setSelectedSlot(null);
      return;
    }
    setSelectedSku(null); setExpandedSku(null);
    setSlots([]); setSelectedSlot(null);
    setAddressFormMessage('');

    setAddressesLoading(true);
    addressAPI.listByPatient(selectedPatient.id)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.content || []);
        setPatientAddresses(list);
        if (list.length > 0) {
          setAddressMode('saved');
          const pick = list.find((a) => a.isDefault) || list[0];
          setSelectedSavedAddressId(pick.id);
          setAddress(mapPatientRecordToLabAddress(pick));
          setNewAddrForm({ ...EMPTY_NEW_ADDR });
        } else {
          setAddressMode('new');
          setSelectedSavedAddressId(null);
          const blank = { ...EMPTY_NEW_ADDR };
          setNewAddrForm(blank);
          setAddress(newAddrFormToLabAddress(blank));
        }
      })
      .catch(() => {
        setPatientAddresses([]);
        setAddressMode('new');
        setSelectedSavedAddressId(null);
        const blank = { ...EMPTY_NEW_ADDR };
        setNewAddrForm(blank);
        setAddress(newAddrFormToLabAddress(blank));
      })
      .finally(() => setAddressesLoading(false));
  }, [selectedPatient]);

  // ── Catalog fetch ──────────────────────────────────────────────────────────
  // 15 Jul 2026: was calling labTestAPI.catalogList() — the raw Thyrocare
  // catalog browse endpoint, which shows every synced SKU (AAROGYAM 1.1,
  // 1.2, 1.3, 1.4, "1 WITH UTSH", etc). Patients were seeing lab-partner
  // product names/pricing instead of Previu's own T1/T2 panels — the exact
  // display-naming bug already tracked in the sprint plan for this page.
  // Switched to labTestAPI.panelPricing(), the same Previu-branded endpoint
  // the chat's get_panel_pricing tool already uses — strips Thyrocare naming
  // entirely, returns fixed Previu panel pricing plus the resolved
  // skuId/skuType/skuName needed later for create_order.
  // 16 Sep 2026: ONCOVERYX retired, replaced by two gender-specific Tumour
  // Panels (TUMOUR_MALE / TUMOUR_FEMALE) — see the gender gate inside
  // fetchCatalog below, which requests only the panel matching
  // selectedPatient.gender. T3 stays excluded — it is still genuinely
  // clinically triggered and requires Dr. Ajay's review before ordering.
  const fetchCatalog = useCallback(async (targetPage, append) => {
    if (!selectedPatient) return;
    setCatalogLoading(true);
    setCatalogError('');
    try {
      // 16 Sep 2026: ONCOVERYX retired — replaced by two gender-specific
      // Tumour Panels (TUMOUR_MALE / TUMOUR_FEMALE, see
      // PanelDefinitionService). Deliberately does NOT use
      // defaultCatalogGenderFromPatient() here — that helper defaults an
      // unrecognized/blank gender to 'MALE' (fine for the Thyrocare slot
      // payload it was built for, which needs *some* value), but guessing
      // here would show the wrong panel to a patient whose gender isn't on
      // file. Unknown gender gets T1/T2 only.
      const gender = String(selectedPatient.gender || '').toUpperCase();
      const requestedPanelCodes = ['T1', 'T2'];
      if (gender === 'MALE')        requestedPanelCodes.push('TUMOUR_MALE');
      else if (gender === 'FEMALE') requestedPanelCodes.push('TUMOUR_FEMALE');
      const res = await labTestAPI.panelPricing({
        patientId: String(selectedPatient.id || ''),
        panels: requestedPanelCodes,
      });
      const data = res.data || {};
      const panels = Array.isArray(data.panels) ? data.panels : [];
      const incoming = panels
        .filter(p => p.skuId) // drop any panel Thyrocare lookup failed for — no SKU to order
        .map(p => ({
          id:   p.skuId,
          // 15 Jul 2026 (fix): p.name from panel-pricing already includes the
          // code — PanelDefinitionService's registry stores it as "T1 —
          // Essential Panel" / "T2 — Standard Panel" directly. Previously
          // prepending p.code again here produced a visible
          // "T2 — T2 — Standard Panel" duplication (confirmed via
          // PatientOrderLabTestPage.jsx, which had the identical bug).
          name: p.name,
          type: p.skuType,
          rate: { currency: 'INR', sellingPrice: p.price },
          noOfTestsIncluded: p.testsIncluded,
          testsIncluded: [], // panel-pricing returns a count, not the itemised list;
                              // "View tests" will show the existing empty-state gracefully
          flags: { isFastingRequired: p.fasting, isHomeCollectible: true },
        }));
      setSkuList(incoming);
      setIsLastPage(true); // only 2-3 curated panels — no pagination
      setPage(1);
    } catch (err) {
      setCatalogError(
        formatLabTestApiErrorBody(err.response?.data) ||
        err.response?.data?.message ||
        'Failed to load Previu panel pricing.'
      );
      setSkuList([]);
    } finally {
      setCatalogLoading(false);
    }
  }, [selectedPatient]);

  useEffect(() => {
    if (selectedPatient) fetchCatalog(1, false);
  }, [selectedPatient, fetchCatalog]);

  // ── Slot search ────────────────────────────────────────────────────────────
  const handleSearchSlots = useCallback(async () => {
    if (!selectedSku)      { setSlotsError('Select a plan first.');        return; }
    if (!selectedPatient)  { setSlotsError('Select a patient first.');     return; }
    if (!appointmentDate)  { setSlotsError('Pick an appointment date.');   return; }
    if (!address.pincode)  { setSlotsError('Enter a pincode.');            return; }
    setSlotsLoading(true);
    setSlotsError('');
    setSelectedSlot(null);
    try {
      const age    = ageFromDob(selectedPatient.dateOfBirth) ?? 30;
      const gender = defaultCatalogGenderFromPatient(selectedPatient.gender);
      const payload = {
        appointmentDate,
        // DEF-030: send the pincode as a STRING. Thyrocare's schema expects a
        // string ("560078") and the backend DTO field is a String for exactly
        // that reason — sending a JSON number was what produced the original
        // "Bad Request" on every slot search. Jackson currently coerces the
        // number back to a String on the way in, which is why this stopped
        // 400ing without this line being touched, but relying on that coercion
        // leaves the contract one config change away from breaking again.
        pincode: String(address.pincode).trim(),
        patients: [{
          name:    `${selectedPatient.firstName || ''} ${selectedPatient.lastName || ''}`.trim(),
          gender,
          age:     String(age),
          ageType: 'YEAR',
          items: [{
            id:   selectedSku.id,
            type: selectedSku.type || 'SSKU',
            name: selectedSku.name,
          }],
        }],
      };
      const res    = await labTestAPI.slotsSearch(payload);
      const apiErr = formatLabTestApiErrorBody(res.data);
      if (apiErr) {
        setSlotsError(apiErr);
        setSlots([]);
      } else {
        const found = extractSlots(res.data);
        setSlots(found);
        // DEF-030/DEF-040: a successful search that returns nothing used to
        // render as an empty panel with no message, which is indistinguishable
        // from "the search never ran" — the retest read it as the feature being
        // broken and as auto-load not firing. Say which one it is.
        if (!found.length) {
          setSlotsError(
            `No collection slots were returned for PIN ${payload.pincode} on `
            + `${formatDisplayDate(appointmentDate)}. Try another date, or check that the `
            + `PIN code is served by the lab partner.`
          );
        }
      }
    } catch (err) {
      setSlotsError(formatSlotsSearchError(err));
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [selectedSku, selectedPatient, appointmentDate, address.pincode]);

  // ── Auto-search slots when the search inputs are ready or change ──────────
  //
  // DEF-040 — "Slots don't auto-load — manual Refresh required. Confusing UX."
  // The hint banner in AddressSlotStep promises "Slots load automatically when
  // you pick a date and have a valid PIN code", and it was telling the truth
  // exactly once per plan.
  //
  // The de-duplication key used to be `${selectedSku.id}|${gender}` — it left
  // out appointmentDate and pincode, which are the two inputs the user actually
  // changes while booking. So the first search fired, the key was stored, and
  // every later date or PIN change re-ran this effect, matched the unchanged
  // key, and returned early. The navigator was left tapping "Refresh slots"
  // after every edit with no indication why.
  //
  // The key's purpose is "don't repeat a search we've already run", so it has to
  // contain every input to that search. Adding the two missing ones is the whole
  // fix — the guard still prevents genuine duplicates (an unrelated re-render,
  // or selectedPatient being replaced by an equal object), it just no longer
  // suppresses searches for inputs it never looked at.
  useEffect(() => {
    if (!selectedSku?.id) {
      lastAutoSlotKeyRef.current = '';
      return;
    }
    if (!selectedPatient || !appointmentDate || !address.pincode) return;
    const gender = defaultCatalogGenderFromPatient(selectedPatient.gender);
    const key = `${selectedSku.id}|${gender}|${appointmentDate}|${address.pincode}`;
    if (lastAutoSlotKeyRef.current === key) return;
    lastAutoSlotKeyRef.current = key;
    void handleSearchSlots();
  }, [selectedSku, selectedPatient, appointmentDate, address.pincode, handleSearchSlots]);

  // ── Build Thyrocare CreateOrderRequest payload ─────────────────────────────
  const buildOrderPayload = () => {
    if (!selectedPatient || !selectedSku || !selectedSlot) return null;
    const age      = ageFromDob(selectedPatient.dateOfBirth) ?? 30;
    const fullName = `${selectedPatient.firstName || ''} ${selectedPatient.lastName || ''}`.trim();
    const start    = slotStart(selectedSlot);

    return {
      address: {
        houseNo:      address.houseNo      || '',
        street:       address.street       || '',
        addressLine1: address.addressLine1 || '',
        addressLine2: address.addressLine2 || '',
        landmark:     address.landmark     || '',
        city:         address.city         || '',
        state:        address.state        || '',
        country:      address.country      || 'India',
        pincode:      Number(address.pincode) || 0,
      },
      email:         selectedPatient.email || '',
      contactNumber: toThyrocareContactNumber(selectedPatient.phone),
      appointment: {
        date:      appointmentDate,
        startTime: start,
        timeZone:  'Asia/Kolkata',
      },
      origin: {
        platform:   'WEB',
        appId:      'previu',
        portalType: 'NAVIGATOR',
        enteredBy:  'navigator',
        source:     'previu-portal',
      },
      referredBy:     { doctorId: '', doctorName: '' },
      paymentDetails: { payType: 'POSTPAID' },
      attributes: {
        remarks:                  '',
        phleboNotes:              '',
        campId:                   '',
        isReportHardCopyRequired: false,
        refOrderNo:               generateTestCode(),
        collectionType:           'HOME_COLLECTION',
        alertMessage:             [],
      },
      config: {
        communication: {
          shareReport:  true,
          shareReceipt: true,
          shareModes:   { whatsapp: true, email: true },
        },
      },
      patients: [{
        name:          fullName,
        gender:        defaultCatalogGenderFromPatient(selectedPatient.gender),
        age:           age,
        ageType:       'YEAR',
        contactNumber: toThyrocareContactNumber(selectedPatient.phone),
        email:         selectedPatient.email || '',
        attributes: {
          ulcUniqueCode:     '',
          patientAddress:    [address.addressLine1, address.city, address.state, address.pincode].filter(Boolean).join(', '),
          externalPatientId: String(selectedPatient.id || ''),
        },
        items: [{
          id:     selectedSku.id,
          type:   selectedSku.type || 'SSKU',
          name:   selectedSku.name,
          origin: { enteredBy: 'navigator', platform: 'WEB' },
        }],
        documents: [],
      }],
      price: {
        discounts: [],
        incentivePasson: { type: 'FLAT', value: '0' },
      },
      orderOptions: { isPdpcOrder: true },
    };
  };

  const priceInPaise = () => {
    const v = selectedSku?.rate?.sellingPrice ?? selectedSku?.rate?.listingPrice;
    return Math.max(100, Math.round(Number(v || 0) * 100));
  };

  // ── Save order record to Previu backend (single attempt) ───────────────────
  const runOrderSave = async (apiBody, failsSoFar) => {
    setOrderPhase('saving');
    setError('');
    try {
      await labTestOrderAPI.create(apiBody);
      navigate('/lab-tests');
    } catch {
      const newFailCount = failsSoFar + 1;
      setOrderAttempt(newFailCount);
      if (newFailCount >= 3) {
        setOrderPhase('refund');
        setError(
          'Order could not be saved after 3 attempts. Your payment was received — ' +
          'please use the Refund button below.'
        );
      } else {
        setOrderPhase('retry');
        setError(
          `Order save failed (attempt ${newFailCount}/3). ` +
          `${3 - newFailCount} retry attempt${3 - newFailCount === 1 ? '' : 's'} remaining.`
        );
      }
    }
  };

  // ── Full payment flow: wallet debit → Thyrocare → save ─────────────────────
  // 27 Jul 2026: was "WhatsApp link → Razorpay popup → Thyrocare → save".
  // Deepu's explicit direction, 27 Jul 2026: navigator bookings are
  // wallet-only, no Razorpay fallback. Steps 3+ below (Thyrocare order
  // creation, /lab-test-orders persistence, retry-on-failure, refund
  // fallback) are UNCHANGED — this only swaps out HOW payment happens, not
  // what happens after it succeeds. Deliberately does NOT call the more
  // elaborate LabTestServiceImpl.createOrderForNavigatorWithWallet backend
  // method (which also calls Thyrocare itself) — that would mean Thyrocare
  // gets called TWICE (once there, once in Step 3 below), creating a real
  // duplicate-order risk. Uses the plain, gateway-agnostic
  // POST /wallet/{patientId}/navigator-debit endpoint instead, which only
  // ever does the debit and nothing else.
  const handleProceedToPayment = async () => {
    setError('');
    setOrderPhase('idle');
    setOrderAttempt(0);
    setRefundResult(null);
    setSavedRzp(null);
    paidViaWalletRef.current  = false;
    savedApiBodyRef.current   = null;
    savedPartnerIdRef.current = null;

    const payload = buildOrderPayload();
    if (!payload) { setError('Please complete patient, plan, and slot.'); return; }

    // Thyrocare's create-order API requires exactly a 10-digit number
    // (formatted '+91-XXXXXXXXXX' by toThyrocareContactNumber). Checked here,
    // before charging the wallet, rather than letting a missing/invalid
    // phone reach Thyrocare as a cryptic 400 after the patient's already
    // been debited — see toThyrocareContactNumber's own comment for the
    // formatting bug this was found alongside.
    if (String(selectedPatient?.phone || '').replace(/\D/g, '').length < 10) {
      setError("This patient's phone number is missing or invalid — please update their profile before booking a lab test.");
      return;
    }

    // 27 Jul 2026: new — client-side pre-check, defense-in-depth alongside
    // the submit button's disabled state (which should normally prevent
    // reaching this code at all when the balance is known to be
    // insufficient). Fails with the same wording the backend would use, so
    // there's no visible inconsistency if this guard is ever what actually
    // catches it (e.g. stale state).
    if (insufficientWalletBalance) {
      setError(
        `Insufficient wallet balance: have ₹${(walletBalanceMinorUnits / 100).toLocaleString('en-IN')}, ` +
        `need ₹${priceRupees.toLocaleString('en-IN')}. Ask the patient to add money to their wallet.`
      );
      return;
    }

    setOrderPhase('paying');

    // ── Step 1: Debit the patient's wallet for the full amount ────────────────
    // Fails closed — if the balance is insufficient, WalletService.debit()
    // (backend) throws a clear "Insufficient wallet balance: have X, need Y"
    // message, surfaced here as-is. Thyrocare is never called in that case.
    try {
      await walletAPI.navigatorDebit(selectedPatient.id, {
        amountMinorUnits: priceInPaise(),
        referenceType:    'LAB_ORDER',
        description:      `Navigator-booked lab test: ${selectedSku?.name || 'Lab Test'}`,
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Payment failed.');
      setOrderPhase('idle');
      return;
    }

    paidViaWalletRef.current = true;

    // ── Step 2: Create Thyrocare partner order + save to Previu DB ────────────
    let partnerOrderId = null;
    let apiBody        = null;

    try {
      const orderRes    = await labTestAPI.createOrder(payload);
      const partnerBody = orderRes.data || {};
      partnerOrderId    = partnerBody.orderId || partnerBody.order_id || null;

      const stored = buildLabTestStorageRecord({
        createOrderPayload: payload,
        partnerResponse:    partnerBody,
        razorpay:           null, // 27 Jul 2026: wallet-only flow — no Razorpay response to attach
        patient:            selectedPatient,
        sku:                selectedSku,
        slot:               selectedSlot,
        appointmentDate,
      });

      apiBody = buildLabTestOrderApiPayload(stored, {
        collectionAddressId: selectedSavedAddressId,
        navigatorId:         user?.id,
      });
    } catch (err) {
      const msg = formatLabTestApiErrorBody(err.response?.data) || err.message || 'Lab-partner order creation failed.';
      setError('Payment received — ' + msg + ' Saving order record...');

      const stored = buildLabTestStorageRecord({
        createOrderPayload: payload,
        partnerResponse:    {},
        razorpay:           null, // 27 Jul 2026: wallet-only flow — no Razorpay response to attach
        patient:            selectedPatient,
        sku:                selectedSku,
        slot:               selectedSlot,
        appointmentDate,
      });

      apiBody = buildLabTestOrderApiPayload(stored, {
        collectionAddressId: selectedSavedAddressId,
        navigatorId:         user?.id,
      });
    }

    if (!apiBody?.patientId) {
      setError('Patient ID is missing or invalid. Cannot save lab test order.');
      setOrderPhase('idle');
      return;
    }

    savedApiBodyRef.current   = apiBody;
    savedPartnerIdRef.current = partnerOrderId;

    await runOrderSave(apiBody, 0);
  };

  // ── Manual retry — POST /api/lab-test-orders only, no Razorpay ────────────
  const handleRetry = () => {
    if (savedApiBodyRef.current) runOrderSave(savedApiBodyRef.current, orderAttempt);
  };

  // ── Refund handler ─────────────────────────────────────────────────────────
  // 28 Jul 2026: no longer branches on paidViaWalletRef — backend always
  // credits `amount` to the wallet now regardless of original payment
  // method, so there's nothing left to branch on here. paidViaWalletRef is
  // still used elsewhere (see the "Wallet charged" vs "Payment received"
  // panel text in LabTestFormPage.js), just not in this payload anymore.
  const handleRequestRefund = async () => {
    setRefundRequesting(true);
    setRefundResult(null);
    try {
      await labTestOrderAPI.refundRequest({
        patientId:         selectedPatient?.id,
        // 28 Jul 2026 — simplified. Backend's requestRefund() now always
        // credits `amount` straight to the wallet, regardless of how the
        // order was originally paid for — the old wallet-vs-razorpay
        // branching (walletRefundAmountMinorUnits vs razorpayPaymentId)
        // is gone on the backend, so it's removed here too. razorpayPaymentId/
        // OrderId/Signature are still sent when available, purely as an
        // audit trail (they no longer trigger any refund logic).
        razorpayPaymentId: savedRzp?.razorpay_payment_id,
        razorpayOrderId:   savedRzp?.razorpay_order_id,
        razorpaySignature: savedRzp?.razorpay_signature,
        amount:            selectedSku?.rate?.sellingPrice ?? selectedSku?.rate?.listingPrice,
        currency:          selectedSku?.rate?.currency || 'INR',
        planId:            selectedSku?.id,
        planName:          selectedSku?.name,
        noOfTests:         selectedSku?.noOfTestsIncluded ?? selectedSku?.testsIncluded?.length,
        patientName:       `${selectedPatient?.firstName || ''} ${selectedPatient?.lastName || ''}`.trim(),
        patientPhone:      selectedPatient?.phone,
        patientEmail:      selectedPatient?.email,
        reason:            'Order creation failed after retries',
      });
      navigate('/lab-tests');
    } catch (err) {
      setRefundResult({
        success: false,
        message:
          formatLabTestApiErrorBody(err.response?.data) ||
          err.message ||
          'Failed to submit refund request. Please contact support directly.',
      });
      setRefundRequesting(false);
    }
  };

  // ── Address helpers ────────────────────────────────────────────────────────
  const clearSlotsAfterAddressChange = () => {
    setSelectedSlot(null);
    lastAutoSlotKeyRef.current = '';
  };

  const selectSavedAddress = (addr) => {
    if (!addr) return;
    setSelectedSavedAddressId(addr.id);
    setAddress(mapPatientRecordToLabAddress(addr));
    clearSlotsAfterAddressChange();
  };

  const switchToSavedAddresses = () => {
    if (!patientAddresses.length) return;
    setAddressMode('saved');
    const pick = patientAddresses.find((a) => a.id === selectedSavedAddressId)
      || patientAddresses.find((a) => a.isDefault)
      || patientAddresses[0];
    selectSavedAddress(pick);
  };

  const switchToNewAddressForm = () => {
    setAddressMode('new');
    setSelectedSavedAddressId(null);
    const blank = { ...EMPTY_NEW_ADDR };
    setNewAddrForm(blank);
    setAddress(newAddrFormToLabAddress(blank));
    clearSlotsAfterAddressChange();
  };

  const setNewAddr = (k) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setNewAddrForm((f) => {
      const next = { ...f, [k]: value };
      setAddress(newAddrFormToLabAddress(next));
      return next;
    });
    clearSlotsAfterAddressChange();
  };

  const handleSaveNewAddressToPatient = async () => {
    if (!selectedPatient) return;
    const f = newAddrForm;
    if (!f.street?.trim() || !f.city?.trim() || !String(f.zipCode || '').trim()) {
      setAddressFormMessage('Street, city, and PIN code are required to save.');
      return;
    }
    setSavingPatientAddress(true);
    setAddressFormMessage('');
    try {
      await addressAPI.create(selectedPatient.id, f);
      const r    = await addressAPI.listByPatient(selectedPatient.id);
      const list = Array.isArray(r.data) ? r.data : (r.data?.content || []);
      setPatientAddresses(list);
      setAddressMode('saved');
      const pick = list.find((a) => a.isDefault) || list[list.length - 1];
      if (pick) selectSavedAddress(pick);
      setAddressFormMessage('Address saved to patient profile.');
    } catch (err) {
      setAddressFormMessage(
        err.response?.data?.message
          || (err.response?.data?.errors ? JSON.stringify(err.response.data.errors) : '')
          || 'Failed to save address.',
      );
    } finally {
      setSavingPatientAddress(false);
    }
  };

  const handleLoadMore = () => {
    if (isLastPage || catalogLoading) return;
    fetchCatalog(page + 1, true);
  };

  const currentStep = !selectedPatient ? 1 : !selectedSku ? 2 : !selectedSlot ? 3 : 4;

  // 27 Jul 2026: new — derived once here rather than recomputed differently
  // in each consumer, so OrderSummary's warning and the submit button's
  // disabled state can never disagree with each other.
  const priceRupees = selectedSku?.rate?.sellingPrice ?? selectedSku?.rate?.listingPrice ?? 0;
  const insufficientWalletBalance =
    walletBalanceMinorUnits !== null && (walletBalanceMinorUnits / 100) < priceRupees;

  return {
    // Patient search
    patientQuery, setPatientQuery,
    patientResults,
    selectedPatient, setSelectedPatient,
    searchLoading,
    showDropdown, setShowDropdown,
    searchRef,
    // Wallet
    walletBalanceMinorUnits,
    insufficientWalletBalance,
    // Catalog
    skuList,
    catalogLoading,
    catalogError,
    isLastPage,
    expandedSku, setExpandedSku,
    selectedSku, setSelectedSku,
    fetchCatalog,
    handleLoadMore,
    // Address
    address,
    patientAddresses,
    addressesLoading,
    addressMode,
    selectedSavedAddressId,
    newAddrForm,
    savingPatientAddress,
    addressFormMessage,
    selectSavedAddress,
    switchToSavedAddresses,
    switchToNewAddressForm,
    setNewAddr,
    handleSaveNewAddressToPatient,
    // Slots
    appointmentDate, setAppointmentDate,
    slots,
    slotsLoading,
    slotsError,
    selectedSlot, setSelectedSlot,
    handleSearchSlots,
    // Order / payment
    error, setError,
    orderPhase,
    orderAttempt,
    savedRzp,
    // 27 Jul 2026: new — lets pages distinguish wallet-paid orders from
    // (future) Razorpay-paid ones, e.g. in the "payment received, order
    // save failed" refund panel.
    paidViaWallet: paidViaWalletRef.current,
    refundRequesting,
    refundResult,
    handleProceedToPayment,
    handleRetry,
    handleRequestRefund,
    // 27 Jul 2026: new
    razorpayEnabledForNavigator,
    // Derived
    currentStep,
  };
}
